import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Layout,
  Card,
  message,
  Switch,
  Space,
  Typography,
  theme,
  FloatButton,
} from "antd";
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  PlusCircleOutlined,
  AppstoreAddOutlined,
} from "@ant-design/icons";
import {
  EmployeeType,
  ScheduleType,
} from "../../components/schedule/components/week/week-types";
import { useWeekView } from "./hooks/useWeekview";

import LocationColorLegend from "./components/LocationColorLegend";
import CloneScheduleModal from "../../components/schedule/modal/CloneScheduleModal";
import dayjs from "dayjs";
import { timeOffStatus } from "../../constants";
import { CompanyContext } from "../../contexts/company-context";
import WeekViewToolbar from "./components/WeekViewToolbar";
import WeekViewGrid from "./components/WeekViewGrid";
import { ScheduleOperation } from "../../serviceoperation/ScheduleOperation";
import TwoWeekViewGrid from "./components/TwoWeekViewGrid";
import { downloadCSV, generateScheduleCSV } from "../../Util/csvExport";
import { EmployeeContext } from "../../contexts/employee-context";
import { AuthenticationContext } from "../../contexts/authentication-context";
import AddScheduleForm from "../../components/schedule/modal/addSchedule";
import { useNavigate } from "react-router";
import { EmployeeOperation } from "../../serviceoperation/EmployeeOperation";

const { Content } = Layout;
const { Text } = Typography;

const WeekView = (): JSX.Element => {
  const navigate = useNavigate();

  const scheduleOps = useMemo(() => new ScheduleOperation(), []);

  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [showColorLegend, setShowColorLegend] = useState(false);
  const { selectedCompany } = useContext(CompanyContext);
  const [employee, setEmployee] = useState<EmployeeType[]>([]);

  const userInfo = useContext(AuthenticationContext)?.userInfo;

  // Enhanced state for new filtering options
  const [selectedLocation, setSelectedLocation] = useState<string>();
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [showPublishedOnly, setShowPublishedOnly] = useState(false);
  const [OpenAddSchedule, setOpenAddSchedule] = useState<boolean>(false);
  const [isManager, setIsManager] = useState<boolean>(false);
  const { employeeData } = useContext(EmployeeContext);
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const [tempAddScheduleData, setTempAddScheduleData] =
    useState<Partial<ScheduleType>>();

  // IS Currently Logged In user Manager or not.
  useMemo(() => {
    setIsManager(employeeData?.hasDirectReport ?? false);
  }, [userInfo, selectedCompany, employeeData]);

  // Get theme token for dark mode compatibility
  const { token } = theme.useToken();

  const {
    currentWeek,
    selectedEmployees,
    scheduleData,
    visibleEmployees,
    viewMode,
    loading,
    location,
    handleEmployeeChange,
    handleSearch,
    navigateWeek,
    goToCurrentWeek,
    cloneSchedule,
    handleViewModeChange,
    loadApiScheduleData,
    //useMockData,
    setLoading,
    getCurrentWeekForApi,
  } = useWeekView();

  // This is so that it sets the default selected
  // location when the page loads....
  useEffect(() => {
    if (location.length > 0) {
      setSelectedLocation(location[0]?.key.toString());
    }
  }, [location]);

  useEffect(() => {
    const empId = userInfo?.employeeCorporation
      ?.filter((x) => x.corporationId === selectedCompany?.corporationId)
      .map((x) => x.employeeId)
      .at(0);
    const fetchEmployees = async () => {
      const employeeOps = new EmployeeOperation();
      const response = await employeeOps.getEmployeeAsync(
        selectedCompany?.corporationId as number,
        1,
        255,
        true,
      );
      setEmployee(
        response.results
          //.filter((x) => x.employeeId !== empId && x.isActive)
          .filter((x) => x.isActive)
          .map((s) => ({
            key: s.employeeId,
            name: `${s.firstName} ${s.lastName}`,
          })),
      );
    };

    fetchEmployees();
  }, [selectedCompany]);

  // API integration effect - this replaces your existing useEffect
  useEffect(() => {
    if (!selectedCompany?.corporationId || !scheduleOps) {
      // If no API props provided, use mock data
      //useMockData();
      return;
    }

    setLoading(true);

    // Use the week from props or current week from hook
    const weekToUse = getCurrentWeekForApi()[0];

    scheduleOps
      .getScheduleAsync(selectedCompany.corporationId, {
        weekOf: weekToUse,
        page: 1,
        pageSize: 255,
        ...(selectedLocation !== undefined &&
          selectedLocation !== "0" &&
          selectedLocation !== "all" && {
            branchId: selectedLocation,
          }),
      })
      .then(({ results }) => {
        // Convert API results to ScheduleType[] format

        // Use provided conversion function
        const groupedResult = groupBy(results, (p) => p.employee.employeeID);
        const scheduleData = Object.keys(groupedResult).map((key) => {
          const root: ScheduleType = {
            key: "",
            name: "",
            initial: "",
            days: Array.from({ length: 7 }, () => []),
          };

          groupedResult[key].forEach((x) => {
            const worktime = new Date(x.workDate);
            worktime.setHours(x.startWorkHour, x.startWorkMinute);
            const workStartTime = worktime.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            });

            worktime.setHours(x.endWorkHour, x.endWorkMinute);
            const workEndTime = worktime.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            });

            const workDay = new Date(x.workDate).getDay();

            root.key = x.employee.employeeID;
            root.name = `${x.employee.firstName} ${x.employee.lastName}`;
            root.initial = x.employee.firstName;
            root.days[workDay].push({
              key: x.scheduleID,
              workDate: dayjs(x.workDate).format("YYYY-MM-DD"),
              time: `${workStartTime} - ${workEndTime}`,
              color: x.branch?.color || "#1890ff",
              isPublished: !(x.isDraft ?? false),
              isAbsent: !!x.isAbsent,
              absentReason: x.absentReason,
              secondcolor: x.isAbsent
                ? categoryColors["absent"]
                : x.timeoffDetail &&
                  x.timeoffDetail.timeOffStatusId === timeOffStatus.APPROVE
                ? categoryColors["timeoff"]
                : x.timeoffDetail &&
                  x.timeoffDetail.timeOffStatusId === timeOffStatus.REQUESTED
                ? categoryColors["timeoffpending"]
                : categoryColors["scheduled"],
              tag: x.isAbsent
                ? "Absent"
                : x.timeoffDetail &&
                  x.timeoffDetail.timeOffStatusId === timeOffStatus.APPROVE
                ? "TimeOff"
                : x.timeoffDetail &&
                  x.timeoffDetail.timeOffStatusId === timeOffStatus.REQUESTED
                ? "TimeOff Pending"
                : "Scheduled",
            });
          });

          return root;
        });

        // Load the converted data into the hook
        loadApiScheduleData(scheduleData);
        message.success("Schedule loaded successfully!");
      })
      .catch((error) => {
        console.error("Error loading schedule:", error);
        message.error("Failed to load schedule data");
        setLoading(false);
      });
  }, [
    selectedLocation,
    selectedCompany,
    scheduleOps,
    currentWeek,
    refreshData,
  ]);

  // GroupBY Function...
  function groupBy<T>(arr: T[], fn: (item: T) => any) {
    return arr.reduce<Record<string, T[]>>((prev, curr) => {
      const groupKey = fn(curr);
      const group = prev[groupKey] || [];
      group.push(curr);
      return { ...prev, [groupKey]: group };
    }, {});
  }

  const categoryColors: any = {
    scheduled: "green",
    timeoffpending: "orange",
    timeoff: "red",
    absent: "volcano",
  };

  // Filter events based on published status
  /*const filteredEvents = showPublishedOnly
    ? scheduleData.filter((event) => event.isPublished !== false)
    : scheduleData;*/

  const filteredEvents = scheduleData;

  // Calculate active filters count
  const getActiveFiltersCount = () => {
    let count = 0;
    if (selectedLocation !== "all") count++;
    if (selectedDepartment !== "all") count++;
    if (selectedEmployees.length > 0) count++;
    if (showPublishedOnly) count++;
    return count;
  };

  const handleExportSchedule = () => {
    // Get the appropriate date range based on view mode
    let dateRange: string[];
    let filename: string;

    if (viewMode === "double") {
      // For two-week view, include both weeks
      const week1Start = dayjs(currentWeek).format("YYYY-MM-DD");
      const week2Start = dayjs(currentWeek).add(1, "week").format("YYYY-MM-DD");
      dateRange = [week1Start, week2Start];
      filename = `schedule-${week1Start}_to_${week2Start}.csv`;
    } else {
      // For single week view, include only current week
      dateRange = getCurrentWeekForApi();
      filename = `schedule-${dayjs(currentWeek).format("YYYY-MM-DD")}.csv`;
    }

    const csvContent = generateScheduleCSV(filteredEvents, dateRange);
    downloadCSV(csvContent, filename);
    message.success("Schedule exported successfully!");
  };

  const handleCloneSchedule = (targetDate: Date) => {
    //cloneSchedule(targetDate);
    message.success("Schedule cloned successfully!");
  };

  const handleAddEvent = (eventData: Partial<any>) => {
    //console.log("What is Event? ", eventData);
    setTempAddScheduleData(eventData);
    setOpenAddSchedule(true);
    //addEvent(eventData);
    //message.success("Event added successfully!");
  };

  const handleLocationChange = (locationId: string) => {
    setSelectedLocation(locationId);
    //message.info(`Filtered by location: ${locationId}`);
  };

  const handleDepartmentChange = (departmentId: string) => {
    setSelectedDepartment(departmentId);
    //message.info(`Filtered by department: ${departmentId}`);
  };

  const handlePublishedFilterChange = (publishedOnly: boolean) => {
    setShowPublishedOnly(publishedOnly);
    //message.info(
    //  publishedOnly
    //    ? "Showing published schedules only"
    //    : "Showing all schedules"
    //);
  };

  const handleBulkAddClick = () => {
    navigate(`/schedule/bulkadd`);
  };

  const onCreate = (values: any) => {
    // Check if the values.scheduleDate is with in selected Week...
    // if it is, then let's reset the selected week only to refresh the data.
    //const isMatch = getCurrentWeekForApi().includes(
    //  dayjs(values.scheduleDate).format("YYYY-MM-DD")
    //);

    const scheduleDate = dayjs(values.scheduleDate);
    const weekStart = dayjs(getCurrentWeekForApi()[0]);
    const isMatch = scheduleDate.isSame(weekStart, "week");

    if (isMatch) {
      setRefreshData(!refreshData);
    }

    setOpenAddSchedule(false);

    //Show Alert successfully inserted schedule???
    message.success("Successfully processed your request.");
  };

  const handleWeekChange = (week: Date) => {
    const weekDiff = Math.round(
      (week.getTime() - currentWeek.getTime()) / (7 * 24 * 60 * 60 * 1000),
    );
    navigateWeek(weekDiff);
  };

  return (
    <Layout
      style={{
        minHeight: "100vh",
        backgroundColor: token.colorBgLayout,
      }}
    >
      <Content style={{ padding: "12px 8px" }}>
        {/* Use consistent max width of 1600px for both single and double week views */}
        <div
          style={{
            width: "100%",
            paddingLeft: window.innerWidth < 768 ? "4px" : "8px",
            paddingRight: window.innerWidth < 768 ? "4px" : "8px",
          }}
        >
          {/*<WeekViewHeader currentWeek={currentWeek} />*/}

          <WeekViewToolbar
            employees={visibleEmployees}
            selectedEmployees={selectedEmployees}
            onEmployeeChange={handleEmployeeChange}
            onSearch={handleSearch}
            onPrevWeek={() => navigateWeek(-1)}
            onNextWeek={() => navigateWeek(1)}
            onCurrentWeek={goToCurrentWeek}
            onExportSchedule={handleExportSchedule}
            onCloneSchedule={() => setIsCloneModalOpen(true)}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            selectedLocation={selectedLocation}
            onLocationChange={handleLocationChange}
            selectedDepartment={selectedDepartment}
            onDepartmentChange={handleDepartmentChange}
            showPublishedOnly={showPublishedOnly}
            onPublishedFilterChange={handlePublishedFilterChange}
            activeFiltersCount={getActiveFiltersCount()}
            currentWeek={currentWeek}
            onWeekChange={handleWeekChange}
            locations={location}
          />

          {/* Color Legend Toggle */}
          <Card
            size="small"
            style={{
              marginBottom: 16,
              borderRadius: 8,
              backgroundColor: token.colorBgContainer,
              borderColor: token.colorBorder,
            }}
            bodyStyle={{ padding: "12px 16px" }}
          >
            <Space
              align="center"
              style={{ width: "100%", justifyContent: "space-between" }}
            >
              <Text strong style={{ fontSize: 14, color: token.colorText }}>
                Location & Department Colors
              </Text>
              <Space align="center">
                <Text style={{ fontSize: 13, color: token.colorTextSecondary }}>
                  {showColorLegend ? "Hide" : "Show"} Legend
                </Text>
                <Switch
                  checked={showColorLegend}
                  onChange={setShowColorLegend}
                  checkedChildren={<EyeOutlined />}
                  unCheckedChildren={<EyeInvisibleOutlined />}
                  size="small"
                />
              </Space>
            </Space>
          </Card>

          {/* Color Legend */}
          <LocationColorLegend visible={showColorLegend} locations={location} />

          <Card
            bodyStyle={{ padding: 0 }}
            style={{
              borderRadius: 12,
              backgroundColor: token.colorBgContainer,
              borderColor: token.colorBorder,
              overflow: "hidden",
            }}
          >
            {viewMode === "single" ? (
              <WeekViewGrid
                employees={visibleEmployees}
                scheduleData={scheduleData}
                currentWeek={currentWeek}
                onAddEvent={handleAddEvent}
                onRefresh={() => setRefreshData((prev) => !prev)}
                loading={loading}
              />
            ) : (
              <TwoWeekViewGrid
                employees={visibleEmployees}
                scheduleData={scheduleData}
                currentWeek={currentWeek}
                onAddEvent={handleAddEvent}
                onRefresh={() => setRefreshData((prev) => !prev)}
                loading={loading}
              />
            )}
          </Card>
        </div>
        {isManager ? (
          <>
            <FloatButton.Group
              trigger="hover"
              type="primary"
              style={{ right: 24 }}
              icon={<PlusCircleOutlined />}
            >
              <FloatButton
                icon={<PlusCircleOutlined />}
                onClick={() => {
                  setOpenAddSchedule(true);
                }}
              />
              <FloatButton
                icon={<AppstoreAddOutlined />}
                onClick={handleBulkAddClick}
              />
            </FloatButton.Group>
            {OpenAddSchedule && (
              <AddScheduleForm
                open={OpenAddSchedule}
                onCreate={onCreate}
                onCancel={() => {
                  setOpenAddSchedule(false);
                }}
                eventData={tempAddScheduleData}
                employee={employee}
              />
            )}
          </>
        ) : null}
      </Content>

      <CloneScheduleModal
        open={isCloneModalOpen}
        onClose={() => setIsCloneModalOpen(false)}
        onClone={handleCloneSchedule}
        sourceWeek={currentWeek}
        corpId={selectedCompany!.corporationId}
      />
    </Layout>
  );
};

export default WeekView;
